import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Layout from '../components/Layout';
import {
  Box, Container, Typography, Card, CardContent, Button, TextField,
  IconButton, Switch, FormControlLabel, Tooltip, CircularProgress,
  Alert, Chip, Grid, MenuItem as MuiMenuItem, Select, InputLabel,
  FormControl, Stack,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  ADMIN_TILE_ICONS,
  ADMIN_TILE_COLOR_SCHEMES,
  DEFAULT_ADMIN_TILES,
  getAdminTileIcon,
  getAdminTileColors,
} from '../config/adminTileIcons';

const ICON_NAMES = Object.keys(ADMIN_TILE_ICONS);
const SCHEME_KEYS = Object.keys(ADMIN_TILE_COLOR_SCHEMES);

export default function AdminTiles() {
  const navigate = useNavigate();
  const [tiles, setTiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const dragIdx = useRef(null);
  const dragOverIdx = useRef(null);

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDoc(doc(db, 'settings', 'adminTiles'));
        if (snap.exists() && snap.data().tiles?.length > 0) {
          const loaded = snap.data().tiles.map(t => ({
            enabled: true, colorScheme: 'blue', icon: 'ManageAccounts', ...t,
          }));
          loaded.sort((a, b) => (a.order || 0) - (b.order || 0));
          setTiles(loaded);
        } else {
          setTiles(DEFAULT_ADMIN_TILES);
        }
      } catch (e) {
        console.error(e);
        setTiles(DEFAULT_ADMIN_TILES);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  function updateTile(idx, field, value) {
    setTiles(arr => arr.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  }

  function addTile() {
    setTiles(arr => [...arr, {
      key: `tile_${Date.now()}`,
      label: 'New Tile',
      desc: '',
      icon: 'ManageAccounts',
      path: '',
      colorScheme: 'blue',
      enabled: true,
      order: arr.length + 1,
    }]);
  }

  function removeTile(idx) {
    if (!window.confirm(`Remove "${tiles[idx].label}"?`)) return;
    setTiles(arr => arr.filter((_, i) => i !== idx));
  }

  async function save() {
    setSaving(true);
    try {
      const withOrder = tiles.map((t, i) => ({ ...t, order: i + 1 }));
      await setDoc(doc(db, 'settings', 'adminTiles'), { tiles: withOrder });
      setTiles(withOrder);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      alert('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  function resetToDefaults() {
    if (!window.confirm('Reset all admin tiles to defaults? This will discard any custom tiles you added.')) return;
    setTiles(DEFAULT_ADMIN_TILES);
  }

  function onDragStart(idx) { dragIdx.current = idx; }
  function onDragOver(e, idx) { e.preventDefault(); dragOverIdx.current = idx; }
  function onDrop() {
    const from = dragIdx.current;
    const to = dragOverIdx.current;
    if (from === null || to === null || from === to) return;
    const updated = [...tiles];
    const [moved] = updated.splice(from, 1);
    updated.splice(to, 0, moved);
    setTiles(updated);
    dragIdx.current = null;
    dragOverIdx.current = null;
  }

  if (loading) return (
    <Layout>
      <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>
    </Layout>
  );

  return (
    <Layout>
      <Box sx={{ bgcolor: '#1E3A5F', color: '#fff', py: 4, mb: 4 }}>
        <Container>
          <Typography variant="h4" fontWeight="bold">🧩 Admin Tile Management</Typography>
          <Typography variant="body2" sx={{ opacity: 0.7, mt: 0.5 }}>
            Manage tiles shown on the /admin page. Changes are live immediately.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ pb: 6 }}>
        {saved && <Alert severity="success" sx={{ mb: 3 }}>Admin tiles saved!</Alert>}

        <Alert severity="info" sx={{ mb: 3 }}>
          Drag tiles to reorder. Disabled tiles are hidden but kept around so you can re-enable them later.
        </Alert>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          {tiles.map((tile, idx) => {
            const IconComp = getAdminTileIcon(tile.icon);
            const scheme = getAdminTileColors(tile.colorScheme);
            return (
              <Card key={tile.key} elevation={2} sx={{
                borderRadius: 3, opacity: tile.enabled ? 1 : 0.65,
                border: '1px solid', borderColor: tile.enabled ? 'primary.main' : 'divider',
              }}
                draggable
                onDragStart={() => onDragStart(idx)}
                onDragOver={e => onDragOver(e, idx)}
                onDrop={onDrop}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Grid container spacing={2} alignItems="flex-start">
                    <Grid sx={{ display: 'flex', alignItems: 'center', pt: '20px !important' }}>
                      <DragIndicatorIcon sx={{ color: 'text.disabled', cursor: 'grab' }} />
                    </Grid>

                    {/* Icon + color preview */}
                    <Grid sx={{ pt: '16px !important' }}>
                      <Box sx={{
                        width: 64, height: 64, borderRadius: 2,
                        bgcolor: scheme.color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        border: '1px solid', borderColor: 'divider',
                      }}>
                        <IconComp sx={{ fontSize: 32, color: scheme.iconColor }} />
                      </Box>
                    </Grid>

                    <Grid size="grow">
                      <Grid container spacing={1.5}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField fullWidth size="small" label="Label" value={tile.label}
                            onChange={e => updateTile(idx, 'label', e.target.value)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Icon</InputLabel>
                            <Select label="Icon" value={tile.icon || 'ManageAccounts'}
                              onChange={e => updateTile(idx, 'icon', e.target.value)}>
                              {ICON_NAMES.map(name => {
                                const Ico = ADMIN_TILE_ICONS[name];
                                return (
                                  <MuiMenuItem key={name} value={name}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <Ico fontSize="small" />
                                      <span>{name}</span>
                                    </Stack>
                                  </MuiMenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <TextField fullWidth size="small" label="Description" value={tile.desc}
                            onChange={e => updateTile(idx, 'desc', e.target.value)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 7 }}>
                          <TextField fullWidth size="small" label="Path" value={tile.path}
                            placeholder="/page-name"
                            onChange={e => updateTile(idx, 'path', e.target.value)} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 5 }}>
                          <FormControl fullWidth size="small">
                            <InputLabel>Color scheme</InputLabel>
                            <Select label="Color scheme" value={tile.colorScheme || 'blue'}
                              onChange={e => updateTile(idx, 'colorScheme', e.target.value)}>
                              {SCHEME_KEYS.map(key => {
                                const s = ADMIN_TILE_COLOR_SCHEMES[key];
                                return (
                                  <MuiMenuItem key={key} value={key}>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: s.color, border: '1px solid #ccc' }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.iconColor, m: '4px' }} />
                                      </Box>
                                      <span>{s.label}</span>
                                    </Stack>
                                  </MuiMenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <FormControlLabel
                            control={<Switch size="small" checked={tile.enabled}
                              onChange={e => updateTile(idx, 'enabled', e.target.checked)} />}
                            label={<Typography variant="caption" color={tile.enabled ? 'success.main' : 'text.disabled'}>
                              {tile.enabled ? 'Enabled' : 'Disabled (hidden)'}
                            </Typography>}
                            sx={{ m: 0 }} />
                        </Grid>
                      </Grid>

                      <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, flexWrap: 'wrap' }}>
                        {!tile.enabled && <Chip label="Disabled" size="small" color="warning" />}
                        {!tile.path && tile.enabled && <Chip label="No path set" size="small" color="error" />}
                      </Box>
                    </Grid>

                    <Grid sx={{ pt: '16px !important' }}>
                      <Tooltip title="Remove tile">
                        <IconButton size="small" color="error" onClick={() => removeTile(idx)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            );
          })}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button variant="contained" startIcon={saving ? null : <SaveIcon />}
            onClick={save} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save Tiles'}
          </Button>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addTile}>Add Tile</Button>
          <Button variant="outlined" color="warning" onClick={resetToDefaults}>Reset to Defaults</Button>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin')}>Back</Button>
        </Box>
      </Container>
    </Layout>
  );
}
